import { useTranslation } from "react-i18next";

import {
  DataTable,
  NARROW_WRAP,
  TableEmptyRow,
  TableGroupRow,
  TableHead,
  TableHeadCell,
} from "@/components/data-table";
import type { ToolGroup } from "@/lib/api";
import { cn } from "@/lib/utils";

import { AUDIENCE_CELL_CLASS, SWITCH_CELL_CLASS, ToolRow } from "./tool-row";
import { groupCountsText, type Serving, toolColumns, type ToolGroupSection } from "./utils";

interface ToolTableProps {
  /** The groups to show, with the tools that match the filters (`filterToolSections`). */
  groups: ToolGroupSection[];
  /** What the connector serves, for why a tool does not reach Claude. */
  serving: Serving;
  /** Whether the table has the "Voor" column, who each tool is for: for the admin roles only. */
  showAudience?: boolean;
  /** False when the backend could not read the viewer's own switches: every switch is then disabled. */
  personalSwitchesKnown: boolean;
  /** Whether a group is open (`isGroupOpen`), and the toggle on its header row. */
  isOpen: (group: ToolGroup) => boolean;
  onToggle: (group: ToolGroup) => void;
  /** Opens a tool's dialog. */
  onOpen: (name: string) => void;
  /** Clears the filters; offered when they leave no tool. */
  onReset?: () => void;
  /** The search as typed, marked in each tool's title, summary and technical name. */
  search?: string;
}

/**
 * The tools in one table, like the dashboard's other tables, without
 * sorting: per group a header row that folds (its title, its summary and
 * what the viewer gets of it, "6 tools, 5 lezen, 1 schrijven"), then the
 * group's tools: read first, then write. Everyone's table has the tool and
 * their own switch ("Voor mij"); the admin roles' has who it is for ("Voor",
 * one badge, `AudienceBadge`) in between, which in the table's phone layout
 * moves under the tool's summary. When the filters leave no tool, one row
 * says so.
 */
export function ToolTable({
  groups,
  serving,
  showAudience = false,
  personalSwitchesKnown,
  isOpen,
  onToggle,
  onOpen,
  onReset,
  search = "",
}: ToolTableProps) {
  const { t } = useTranslation();
  const columns = toolColumns(showAudience);
  return (
    <DataTable label={t("nav.tools")}>
      <TableHead>
        <TableHeadCell>{t("tools.column.tool")}</TableHeadCell>
        {/* As narrow as its badges, so a tool's title line keeps its room. */}
        {columns.includes("audience") && (
          <TableHeadCell narrow="hide" className={AUDIENCE_CELL_CLASS}>
            {t("tools.column.audience")}
          </TableHeadCell>
        )}
        {/* In the phone layout the column's name may take two lines ("Voor / mij"), so the table fits. */}
        <TableHeadCell
          align="right"
          className={cn("whitespace-nowrap", NARROW_WRAP, SWITCH_CELL_CLASS)}
        >
          {t("tools.column.personal")}
        </TableHeadCell>
      </TableHead>
      {groups.map((group) => {
        const expanded = isOpen(group.id);
        return (
          <tbody key={group.id} data-group={group.id}>
            <TableGroupRow
              title={group.title}
              summary={group.summary}
              colSpan={columns.length}
              collapse={{ expanded, onToggle: () => onToggle(group.id) }}
              aside={groupCountsText(group.counts)}
            />
            {expanded &&
              group.tools.map((tool) => (
                <ToolRow
                  key={tool.name}
                  tool={tool}
                  serving={serving}
                  showAudience={showAudience}
                  personalSwitchesKnown={personalSwitchesKnown}
                  search={search}
                  onOpen={onOpen}
                />
              ))}
          </tbody>
        );
      })}
      {groups.length === 0 && (
        <tbody>
          <TableEmptyRow colSpan={columns.length} onReset={onReset}>
            {t("tools.filter.empty")}
          </TableEmptyRow>
        </tbody>
      )}
    </DataTable>
  );
}
